// ✅ Header animates first, table animates after the header finishes

import { Center, Collapse, Flex, Image, List, Spinner, Text, useDisclosure } from '@chakra-ui/react'
import { Button as UbiButton } from '@ubirider/pick-component-library'
import { useMemo, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { motion } from 'motion/react'
import { Trip } from './components/Trip'
import { NewBillingModal } from '../../../../modals/NewBillingModal/NewBillingModal'
import { SelectBillingModal } from '../../../../modals/SelectBillingModal/SelectBillingModal'
import { FiltersModal } from '../../../../modals/FiltersModal/FiltersModal'
import trainForbidden from '../../../../assets/train_forbidden.svg'
import { useBilling, useCards, useTripsFiltered } from '../../../../context/DataProvider'

const MFlex = motion.create(Flex)
const MText = motion.create(Text)
const MCenter = motion.create(Center)
const MList = motion.create(List)

export const TravelHistoryTable = ({ home }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { pickCard, loading: cardsLoading } = useCards()
  const { trips: sortedTrips, loading: tripsLoading } = useTripsFiltered({ home })
  const { billing, loading: billingLoading } = useBilling()
  const loading = cardsLoading || tripsLoading || billingLoading

  const [modalTrips, setModalTrips] = useState([])
  const [checkedTrips, setCheckedTrips] = useState([])
  const [alert, setAlert] = useState(false)
  const [alert2, setAlert2] = useState(false)

  const { isOpen, onOpen, onClose } = useDisclosure()
  const { isOpen: isOpenSB, onOpen: onOpenSB, onClose: onCloseSB } = useDisclosure()
  const { isOpen: isOpenFilters, onOpen: onOpenFilters, onClose: onCloseFilters } = useDisclosure()

  // section animation once
  const [hasAnimatedSection, setHasAnimatedSection] = useState(false)
  const sectionInitial = hasAnimatedSection ? false : 'hidden'

  // header-first sequencing
  const [headerReady, setHeaderReady] = useState(false)
  const [hasAnimatedRows, setHasAnimatedRows] = useState(false)

  useEffect(() => {
    // whenever we go from loading -> not loading the first time, re-run the header-first sequence
    if (!loading && !hasAnimatedRows) setHeaderReady(false)
  }, [loading, hasAnimatedRows])

  const handleCheckTrip = newTrip => {
    setCheckedTrips(prev => {
      const exists = prev.includes(newTrip)
      if (!exists && prev.length < 5) {
        const next = [...prev, newTrip]
        setAlert2(next.length > 0)
        setAlert(true)
        return next
      }
      const next = prev.filter(t => t !== newTrip)
      if (next.length <= 1) setAlert(false)
      if (next.length <= 2) setAlert2(false)
      return next
    })
  }

  const handleOpenModal = tripsArg => {
    setModalTrips(tripsArg)
    if (billing.length > 0) onOpenSB()
    else onOpen()
  }

  const handleCloseModal = () => {
    onClose()
    if (billing.length > 0) onOpenSB()
  }

  const variants = useMemo(() => ({
    container: {
      hidden: { opacity: 0 },
      visible: { opacity: 1 }
    },
    section: {
      hidden: { opacity: 0, y: 10 },
      visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 360, damping: 26 } }
    },
    header: {
      hidden: { opacity: 0, y: 8 },
      visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 360, damping: 28 } }
    },
    rowsContainer: {
      hidden: { opacity: 0 },
      visible: {
        opacity: 1,
        transition: { when: 'beforeChildren', staggerChildren: 0.08, delayChildren: 0.05 }
      }
    },
    row: {
      hidden: { opacity: 0, y: 8 },
      visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 340, damping: 24 } }
    },
    buttonLate: {
      hidden: { opacity: 0, y: 8 },
      visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 360, damping: 26, delay: 0.25 } }
    }
  }), [])

  const hasTrips = sortedTrips && sortedTrips.length > 0
  const keyOf = t => t?.id ?? `${t?.date ?? ''}|${t?.cardPAN ?? ''}|${t?.amount ?? ''}|${t?.merchant ?? ''}`

  return (
    <MFlex
      direction='column'
      gap='32px'
      width='100%'
      initial={sectionInitial}
      animate='visible'
      variants={variants.container}
      onAnimationComplete={() => setHasAnimatedSection(true)}
    >
      <MFlex
        bg={{ sm: '', md: 'neutral.100', lg: 'neutral.100', xl: 'neutral.100' }}
        padding={{ sm: '0', md: '24px', lg: '24px', xl: '24px' }}
        rounded='8px'
        gap='24px'
        direction='column'
        variants={variants.section}
      >
        <MFlex gap='28px' direction='column' flex='1' variants={variants.container}>
          {/* Header animates first. When it's done, we flip headerReady=true */}
          <MFlex
            flex='1'
            align='center'
            justify='space-between'
            variants={variants.header}
            onAnimationComplete={() => setHeaderReady(true)}
          >
            <MText fontWeight='700' fontSize='24px' variants={variants.header}>
              {home ? t('demo.last_trips') : t('travel_history.travel_history')}
            </MText>
          </MFlex>

          {loading ? (
            <MCenter minH='240px' variants={variants.section} initial={false}>
              <Spinner thickness='6px' color='neutral.500' size='xl' />
            </MCenter>
          ) : hasTrips ? (
            <MFlex direction='column' flex='1' variants={variants.container} initial={false}>
              <MText fontSize='14px' color='neutral.800' mb='24px' variants={variants.header} initial={false}>
                {t('demo.travel_history_description')}
              </MText>

              <Collapse in={alert2} animateOpacity>
                <MFlex direction='row' justifyContent='flex-end' gap='16px' variants={variants.header} initial={false}>
                  <motion.div variants={variants.buttonLate} initial={false} animate='visible'>
                    <UbiButton
                      onClick={() => handleOpenModal(checkedTrips)}
                      loading={loading}
                      style='outline'
                      type='primary'
                      label={t('travel_history.change_tax_id')}
                    />
                  </motion.div>
                  <motion.div variants={variants.buttonLate} initial={false} animate='visible'>
                    <UbiButton
                      loading={loading}
                      style='solid'
                      type='primary'
                      label={t('travel_history.download_invoice')}
                    />
                  </motion.div>
                </MFlex>
              </Collapse>

              {/* Table header (still part of header group so it appears before rows) */}
              <MFlex
                w='100%'
                height='48px'
                px='16px'
                align='center'
                justify='center'
                bg='white'
                css={'box-shadow: 0 0 20px rgba(0,0,0,0.1)'}
                variants={variants.header}
                initial={false}
              >
                <Text fontSize='14px' fontWeight='700' flex='3'>
                  {t('travel_history.date')}
                </Text>
                <Text fontSize='14px' fontWeight='700' flex='2'>
                  {t('demo.transport')}
                </Text>
                <Text fontSize='14px' fontWeight='700' flex='3'>
                  {t('travel_history.payment_method')}
                </Text>
                <Text fontSize='14px' fontWeight='700' flex='2'>
                  {t('common.tax_id')}
                </Text>
                <Text fontSize='14px' fontWeight='700' flex='2'>
                  {t('demo.merchant')}
                </Text>
                <Text fontSize='14px' fontWeight='700' flex='2' textAlign='right'>
                  {t('travel_history.amount')}
                </Text>
                <Flex flex='1' />
              </MFlex>

              {/* Rows wait until the header finishes: animate only after headerReady */}
              <MList
                variants={variants.rowsContainer}
                initial={hasAnimatedRows ? false : 'hidden'}
                animate="visible"
                onAnimationComplete={() => setHasAnimatedRows(true)}
              >
                {sortedTrips.map((trip, i) => {
                  const key = keyOf(trip)
                  return (
                    // IMPORTANT: remove initial/animate here; let parent handle it
                    <motion.li key={key} variants={variants.row} layout="position">
                      <Trip
                        pickCard={pickCard}
                        billing={billing}
                        trip={trip}
                        isChecked={checkedTrips.includes(trip)}
                        onChange={() => handleCheckTrip(trip)}
                        onEdit={() => handleOpenModal([trip])}
                      />
                    </motion.li>
                  )
                })}
              </MList>
            </MFlex>
          ) : (
            <MFlex
              direction='column'
              my={{ sm: '', md: '48px', lg: '48px', xl: '48px' }}
              mx={{ sm: '', md: '96px', lg: '96px', xl: '96px' }}
              justifyContent='center'
              alignItems='center'
              textAlign='center'
              gap='24px'
              variants={variants.section}
              initial={false}
            >
              <motion.div variants={variants.header} initial={false} animate='visible'>
                <Image src={trainForbidden} height='112px' />
              </motion.div>
              <MText fontSize='14px' color='neutral.800' textAlign='center' variants={variants.header} initial={false} animate='visible'>
                {t('demo.travel_history_empty_state')}
              </MText>
              <motion.div variants={variants.buttonLate} initial={false} animate='visible'>
                <UbiButton
                  onClick={() => navigate('/faqs', { state: true })}
                  loading={loading}
                  style='solid'
                  type='primary'
                  label={t('travel_history.know_more')}
                />
              </motion.div>
            </MFlex>
          )}
        </MFlex>

        {home && hasTrips && (
          <MFlex
            align='center'
            justify='space-between'
            padding={{ sm: '', md: '', lg: '0 16px', xl: '0 16px' }}
            direction={{ sm: 'column', md: 'column', lg: 'row', xl: 'row' }}
            gap={{ sm: '12px', md: '12px', lg: '48px', xl: '48px' }}
            variants={variants.section}
            initial={false}
          >
            <MText
              alignSelf={{ sm: 'center', md: 'center', lg: '', xl: '' }}
              color='neutral.600'
              fontWeight='400'
              fontSize='14px'
              variants={variants.header}
              initial={false}
            >
              {t('travel_history.displaying_latest_trips')}
            </MText>
            <MFlex
              justify={{ sm: 'center', md: 'center', lg: 'flex-start', xl: 'flex-start' }}
              variants={variants.header}
              initial={false}
            >
              <motion.div variants={variants.buttonLate} initial={false} animate='visible'>
                <UbiButton
                  onClick={() => navigate('/travel-history')}
                  size='small'
                  style='ghost'
                  type='primary'
                  label={t('travel_history.see_all_trips')}
                />
              </motion.div>
            </MFlex>
          </MFlex>
        )}
      </MFlex>

      <FiltersModal onOpen={onOpenFilters} onClose={onCloseFilters} isOpen={isOpenFilters} />
      <NewBillingModal onOpen={onOpen} onClose={handleCloseModal} isOpen={isOpen} blueInfo />
      <SelectBillingModal onOpen={onOpenSB} onClose={onCloseSB} isOpen={isOpenSB} onOpenBM={onOpen} trips={modalTrips} />
    </MFlex>
  )
}